import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Switch,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon, Visibility, VisibilityOff, Delete as DeleteIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from 'axios';
import Calendar from '../../components/shared/Calendar/Calendar';
import { useVendorAuth } from '../../contexts/VendorAuthContext';
import {
  getVendorAvailability,
  updateVendorAvailability,
  getUnavailableDates,
  addUnavailableDate,
  removeUnavailableDate,
  DaySchedule,
  UnavailableDate,
} from '../../api/availabilityService';
import { DESIGN_TOKENS } from '../../styles/constants';

interface BusinessDay {
  day: string;
  enabled: boolean;
  from: string;
  to: string;
}

interface DisplayUnavailableDate {
  id: string;
  description: string;
  dates: string;
}

const Settings: React.FC = () => {
  const { vendor } = useVendorAuth();
  
  // State management
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [editScheduleOpen, setEditScheduleOpen] = useState(false);
  const [addUnavailabilityOpen, setAddUnavailabilityOpen] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [isProfileDeactivated, setIsProfileDeactivated] = useState(false);
  
  // Loading states
  const [loadingSchedule, setLoadingSchedule] = useState(true);
  const [loadingUnavailable, setLoadingUnavailable] = useState(true);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [addingUnavailable, setAddingUnavailable] = useState(false);
  
  // Password form state
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Schedule state
  const [sameHoursForAll, setSameHoursForAll] = useState(true);
  const [businessDays, setBusinessDays] = useState<BusinessDay[]>([
    { day: 'Mon', enabled: false, from: '10:00', to: '23:59' },
    { day: 'Tue', enabled: true, from: '10:00', to: '23:59' },
    { day: 'Wed', enabled: true, from: '10:00', to: '23:59' },
    { day: 'Thu', enabled: true, from: '10:00', to: '23:59' },
    { day: 'Fri', enabled: true, from: '10:00', to: '23:59' },
    { day: 'Sat', enabled: true, from: '10:00', to: '23:59' },
    { day: 'Sun', enabled: true, from: '14:00', to: '23:59' },
  ]);
  
  // Unavailable dates state
  const [unavailableDates, setUnavailableDates] = useState<DisplayUnavailableDate[]>([]);
  const [unavailableDatesData, setUnavailableDatesData] = useState<UnavailableDate[]>([]);
  
  const [repeatPeriod, setRepeatPeriod] = useState<'none' | 'monthly' | 'yearly'>('yearly');
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [firstCalendarMonth, setFirstCalendarMonth] = useState(new Date().getMonth());
  const [firstCalendarYear, setFirstCalendarYear] = useState(new Date().getFullYear());
  const [secondCalendarMonth, setSecondCalendarMonth] = useState((new Date().getMonth() + 1) % 12);
  const [secondCalendarYear, setSecondCalendarYear] = useState(
    new Date().getMonth() === 11 ? new Date().getFullYear() + 1 : new Date().getFullYear()
  );

  // Fetch availability schedule
  const fetchSchedule = useCallback(async () => {
    if (!vendor?.vendorId) {
      console.error('❌ No vendorId found in context:', vendor);
      setLoadingSchedule(false);
      return;
    }

    console.log('✅ Fetching schedule for vendorId:', vendor.vendorId);

    try {
      setLoadingSchedule(true);
      const response = await getVendorAvailability(vendor.vendorId);
      
      console.log('📦 Availability response:', response);
      console.log('  ✓ Success:', response?.success);
      console.log('  ✓ Has data:', !!response?.data);
      console.log('  ✓ Has weeklySchedule:', !!response?.data?.weeklySchedule);
      console.log('  ✓ Schedule length:', response?.data?.weeklySchedule?.length);
      
      if (!response) {
        console.error('❌ No response from server');
        toast.error('No response from server');
        return;
      }

      if (!response.success) {
        console.error('❌ Request failed:', response.message);
        toast.error(response.message || 'Failed to load schedule');
        return;
      }

      if (!response.data) {
        console.error('❌ No data in response');
        toast.error('No data received from server');
        return;
      }

      if (!response.data.weeklySchedule) {
        console.warn('⚠️ No weeklySchedule in response, using default');
        return;
      }

      if (response.success && response.data.weeklySchedule) {
        // Transform API data to component format
        const dayMap: { [key: string]: string } = {
          'Monday': 'Mon',
          'Tuesday': 'Tue',
          'Wednesday': 'Wed',
          'Thursday': 'Thu',
          'Friday': 'Fri',
          'Saturday': 'Sat',
          'Sunday': 'Sun',
        };

        const transformedDays = response.data.weeklySchedule.map(apiDay => ({
          day: dayMap[apiDay.day] || apiDay.day,
          enabled: apiDay.isAvailable,
          from: apiDay.startTime,
          // Convert 24:00 to 23:59 as HTML time inputs don't support 24:00
          to: apiDay.endTime === '24:00' ? '23:59' : apiDay.endTime,
        }));

        setBusinessDays(transformedDays);
        
        // Check if all enabled days have same hours
        const enabledDays = transformedDays.filter(d => d.enabled);
        if (enabledDays.length > 0) {
          const firstDay = enabledDays[0];
          const allSame = enabledDays.every(d => d.from === firstDay.from && d.to === firstDay.to);
          setSameHoursForAll(allSame);
        }
      }
    } catch (err) {
      console.error('Error fetching schedule:', err);
      if (axios.isAxiosError(err) && err.response?.status !== 404) {
        toast.error('Failed to load availability schedule');
      }
    } finally {
      setLoadingSchedule(false);
    }
  }, [vendor]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  // Fetch unavailable dates
  useEffect(() => {
    const fetchUnavailableDates = async () => {
      if (!vendor?.vendorId) {
        console.error('❌ No vendorId for unavailable dates');
        setLoadingUnavailable(false);
        return;
      }

      console.log('✅ Fetching unavailable dates for vendorId:', vendor.vendorId);

      try {
        setLoadingUnavailable(true);
        const response = await getUnavailableDates(vendor.vendorId);
        
        console.log('📦 Unavailable dates response:', response);
        console.log('  ✓ Success:', response?.success);
        console.log('  ✓ Has data:', !!response?.data);
        console.log('  ✓ Has unavailableDates:', !!response?.data?.unavailableDates);
        console.log('  ✓ Dates count:', response?.data?.unavailableDates?.length);
        
        if (!response || !response.success) {
          console.error('❌ Failed to fetch unavailable dates:', response?.message);
          return;
        }

        if (response.success && response.data.unavailableDates) {
          setUnavailableDatesData(response.data.unavailableDates);
          
          // Transform to display format
          const displayDates = response.data.unavailableDates.map(date => {
            const start = new Date(date.startDate);
            const end = new Date(date.endDate);
            const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            
            let description = `${startStr}–${endStr}`;
            if (date.repeatType === 'monthly') {
              description += ' every month';
            } else if (date.repeatType === 'yearly') {
              description += ' every year';
            } else {
              description += `, ${start.getFullYear()}`;
            }

            return {
              id: date.availabilityId,
              description,
              dates: description,
            };
          });

          setUnavailableDates(displayDates);
        }
      } catch (err) {
        console.error('Error fetching unavailable dates:', err);
        if (axios.isAxiosError(err) && err.response?.status !== 404) {
          toast.error('Failed to load unavailable dates');
        }
      } finally {
        setLoadingUnavailable(false);
      }
    };

    fetchUnavailableDates();
  }, [vendor?.vendorId]);

  // Handlers
  const handleChangePassword = async () => {
    if (!vendor?.vendorId) {
      toast.error('Vendor ID not found');
      return;
    }

    // Validate passwords
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('All password fields are required');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters long');
      return;
    }

    if (newPassword === currentPassword) {
      toast.error('New password must be different from current password');
      return;
    }

    try {
      const { vendorManagementService } = await import('../../api/vendorManagementService');
      
      await vendorManagementService.changePassword(vendor.vendorId, {
        currentPassword,
        newPassword,
      });

      setChangePasswordOpen(false);
      setShowSuccessBanner(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setShowSuccessBanner(false), 5000);
      toast.success('Passwor changed successfully');
    } catch (err) {
      console.error('Error changing password:', err);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 401) {
          toast.error('Current password is incorrect');
        } else if (err.response?.data?.message) {
          toast.error(err.response.data.message);
        } else {
          toast.error('Failed to change password. Please try again.');
        }
      } else {
        toast.error('Failed to change password. Please try again.');
      }
    }
  };

  const handleSaveSchedule = async () => {
    if (!vendor?.vendorId) return;

    // Validate: at least one day must be enabled
    const hasEnabledDay = businessDays.some(day => day.enabled);
    if (!hasEnabledDay) {
      toast.error('Please enable at least one business day');
      return;
    }

    // Validate: enabled days must have valid time ranges
    for (const day of businessDays) {
      if (day.enabled) {
        if (!day.from || !day.to) {
          toast.error(`Please set hours for ${day.day}`);
          return;
        }
        // Validate time range
        const [fromHours, fromMinutes] = day.from.split(':').map(Number);
        const [toHours, toMinutes] = day.to.split(':').map(Number);
        const fromTotal = fromHours * 60 + fromMinutes;
        const toTotal = toHours * 60 + toMinutes;
        if (fromTotal >= toTotal) {
          toast.error(`Invalid time range for ${day.day}: opening time must be before closing time`);
          return;
        }
      }
    }

    setSavingSchedule(true);
    try {
      // Transform component format to API format
      const dayMap: { [key: string]: string } = {
        'Mon': 'Monday',
        'Tue': 'Tuesday',
        'Wed': 'Wednesday',
        'Thu': 'Thursday',
        'Fri': 'Friday',
        'Sat': 'Saturday',
        'Sun': 'Sunday',
      };

      const weeklySchedule: DaySchedule[] = businessDays.map(day => ({
        day: dayMap[day.day] || day.day,
        isAvailable: day.enabled,
        startTime: day.from,
        // Convert 24:00 to 23:59 as HTML time inputs don't support 24:00
        endTime: day.to === '24:00' ? '23:59' : day.to,
      }));

      await updateVendorAvailability(vendor.vendorId, weeklySchedule);
      
      // Refetch to confirm save
      const response = await getVendorAvailability(vendor.vendorId);
      if (response.success && response.data.weeklySchedule) {
        const dayMapReverse: { [key: string]: string } = {
          'Monday': 'Mon',
          'Tuesday': 'Tue',
          'Wednesday': 'Wed',
          'Thursday': 'Thu',
          'Friday': 'Fri',
          'Saturday': 'Sat',
          'Sunday': 'Sun',
        };

        const transformedDays = response.data.weeklySchedule.map(apiDay => ({
          day: dayMapReverse[apiDay.day] || apiDay.day,
          enabled: apiDay.isAvailable,
          from: apiDay.startTime,
          to: apiDay.endTime === '24:00' ? '23:59' : apiDay.endTime,
        }));

        setBusinessDays(transformedDays);
      }
      
      toast.success('Schedule updated successfully');
      setEditScheduleOpen(false);
    } catch (err) {
      console.error('Error saving schedule:', err);
      if (axios.isAxiosError(err)) {
        if (err.code === 'ERR_NETWORK') {
          toast.error('Network error. Please check your connection.');
        } else {
          toast.error(err.response?.data?.message || 'Failed to update schedule');
        }
      } else {
        toast.error('Failed to update schedule. Please try again.');
      }
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleDateSelect = (date: Date) => {
    setSelectedStartDate(date);
    setSelectedEndDate(null);
  };

  const handleRangeSelect = (startDate: Date, endDate: Date) => {
    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);
  };

  const handleFirstCalendarMonthChange = (month: number, year: number) => {
    setFirstCalendarMonth(month);
    setFirstCalendarYear(year);
  };

  const handleSecondCalendarMonthChange = (month: number, year: number) => {
    setSecondCalendarMonth(month);
    setSecondCalendarYear(year);
  };

  const handleBlockDates = async () => {
    if (!vendor?.vendorId || !selectedStartDate || !selectedEndDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    // Validate: don't allow past dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selectedStartDate < today) {
      toast.error('Cannot block dates in the past');
      return;
    }

    setAddingUnavailable(true);
    try {
      // Format dates as YYYY-MM-DD (backend expects this format)
      const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const response = await addUnavailableDate({
        vendorId: vendor.vendorId,
        startDate: formatDate(selectedStartDate),
        endDate: formatDate(selectedEndDate),
        repeatType: repeatPeriod,
        reason: 'Vendor unavailable',
      });

      if (response.success) {
        // Refetch unavailable dates to get updated list
        const updatedResponse = await getUnavailableDates(vendor.vendorId);
        
        if (updatedResponse.success && updatedResponse.data.unavailableDates) {
          setUnavailableDatesData(updatedResponse.data.unavailableDates);
          
          // Transform to display format
          const displayDates = updatedResponse.data.unavailableDates.map(date => {
            const start = new Date(date.startDate);
            const end = new Date(date.endDate);
            const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            
            let description = `${startStr}–${endStr}`;
            if (date.repeatType === 'monthly') {
              description += ' every month';
            } else if (date.repeatType === 'yearly') {
              description += ' every year';
            } else {
              description += `, ${start.getFullYear()}`;
            }

            return {
              id: date.availabilityId,
              description,
              dates: description,
            };
          });

          setUnavailableDates(displayDates);
        }

        toast.success('Unavailable period added successfully');
        
        // Reset selection
        setSelectedStartDate(null);
        setSelectedEndDate(null);
        setAddUnavailabilityOpen(false);
      }
    } catch (err) {
      console.error('Error adding unavailable date:', err);
      if (axios.isAxiosError(err)) {
        if (err.code === 'ERR_NETWORK') {
          toast.error('Network error. Please check your connection.');
        } else {
          toast.error(err.response?.data?.message || 'Failed to add unavailable period');
        }
      } else {
        toast.error('Failed to add unavailable period. Please try again.');
      }
    } finally {
      setAddingUnavailable(false);
    }
  };

  const handleDeleteUnavailableDate = async (id: string) => {
    if (!vendor?.vendorId) return;

    try {
      await removeUnavailableDate(vendor.vendorId, id);
      setUnavailableDates(unavailableDates.filter(date => date.id !== id));
      setUnavailableDatesData(unavailableDatesData.filter(date => date.availabilityId !== id));
      toast.success('Unavailable period removed successfully');
    } catch (err) {
      console.error('Error removing unavailable date:', err);
      if (axios.isAxiosError(err)) {
        toast.error(err.response?.data?.message || 'Failed to remove unavailable period');
      } else {
        toast.error('Failed to remove unavailable period. Please try again.');
      }
    }
  };

  const handleToggleDeactivation = () => {
    // TODO: Implement profile deactivation API call
    setIsProfileDeactivated(!isProfileDeactivated);
  };

  const handleDayToggle = (index: number) => {
    const newDays = [...businessDays];
    newDays[index].enabled = !newDays[index].enabled;
    setBusinessDays(newDays);
  };

  const handleTimeChange = (index: number, field: 'from' | 'to', value: string) => {
    const newDays = [...businessDays];
    newDays[index][field] = value;
    setBusinessDays(newDays);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#FFFFFF',
      }}
    >
      <Box
        sx={{
          padding: '40px 80px',
          maxWidth: '1440px',
          margin: '0 auto',
          width: '100%',
          '@media (max-width: 1200px)': {
            padding: '32px 40px',
          },
          '@media (max-width: 768px)': {
            padding: '20px',
          },
        }}
      >
        {/* Success Banner */}
        {showSuccessBanner && (
          <Box sx={{
            position: 'fixed',
            top: 920,
            right: 24,
            width: '440px',
            backgroundColor: '#F4DBC5',
            borderRadius: '16px',
            padding: '16px 24px',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            boxShadow: '0px 40px 60px 0px rgba(26, 109, 109, 0.1)',
            zIndex: 1000,
          }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ 
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '16px',
                fontWeight: 700,
                color: DESIGN_TOKENS.colors.text,
                marginBottom: '4px',
              }}>
                Password updated
              </Typography>
              <Typography sx={{ 
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '16px',
                color: DESIGN_TOKENS.colors.text,
              }}>
                You can now log in with your new password
              </Typography>
            </Box>
            <IconButton 
              onClick={() => setShowSuccessBanner(false)}
              sx={{
                backgroundColor: '#F6F8F8',
                borderRadius: '16px',
                width: 32,
                height: 32,
                '&:hover': { backgroundColor: '#E5E9E9' },
              }}
            >
              <CloseIcon sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.text }} />
            </IconButton>
          </Box>
        )}

        {/* Page Title */}
        <Box sx={{ marginBottom: '32px' }}>
          <Typography variant="h1" sx={{
            fontFamily: DESIGN_TOKENS.typography.fontFamily,
            fontSize: '44px',
            fontWeight: 700,
            lineHeight: 1.15,
            color: DESIGN_TOKENS.colors.text,
            marginBottom: '8px',
            '@media (max-width: 768px)': {
              fontSize: '32px',
            },
          }}>
            Settings
          </Typography>
          <Typography sx={{
            fontFamily: DESIGN_TOKENS.typography.fontFamily,
            fontSize: '16px',
            color: DESIGN_TOKENS.colors.text,
            '@media (max-width: 768px)': {
              fontSize: '14px',
            },
          }}>
            Control your account settings
          </Typography>
        </Box>

        {/* Main Content */}
        <Box sx={{ display: 'flex', gap: '40px' }}>
          {/* Left Column */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '60px' }}>
            {/* Account Details Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Typography sx={{
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '20px',
                fontWeight: 700,
                color: DESIGN_TOKENS.colors.text,
              }}>
                Account details
              </Typography>
              
              <Box>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  padding: '4px 0',
                  borderBottom: '1px solid #F1F4F4',
                }}>
                  <Typography sx={{ 
                    fontFamily: DESIGN_TOKENS.typography.fontFamily,
                    fontSize: '16px',
                    color: DESIGN_TOKENS.colors.text,
                  }}>
                    Email address:
                  </Typography>
                  <Typography sx={{ 
                    fontFamily: DESIGN_TOKENS.typography.fontFamily,
                    fontSize: '16px',
                    color: DESIGN_TOKENS.colors.text,
                  }}>
                    {vendor?.email || 'N/A'}
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  padding: '4px 0',
                  borderBottom: '1px solid #F1F4F4',
                }}>
                  <Typography sx={{ 
                    fontFamily: DESIGN_TOKENS.typography.fontFamily,
                    fontSize: '16px',
                    color: DESIGN_TOKENS.colors.text,
                  }}>
                    Password
                  </Typography>
                  <Typography sx={{ 
                    fontFamily: DESIGN_TOKENS.typography.fontFamily,
                    fontSize: '16px',
                    color: DESIGN_TOKENS.colors.text,
                  }}>
                    •••••••••
                  </Typography>
                </Box>
              </Box>
              
              <Button
                variant="outlined"
                onClick={() => setChangePasswordOpen(true)}
                sx={{
                  width: 'fit-content',
                  height: '56px',
                  borderRadius: '16px',
                  border: '1px solid #67314F',
                  color: DESIGN_TOKENS.colors.primary,
                  fontFamily: DESIGN_TOKENS.typography.fontFamily,
                  fontSize: '16px',
                  fontWeight: 700,
                  padding: '2px 32px 0px',
                  textTransform: 'none',
                  '&:hover': {
                    border: '1px solid #67314F',
                    backgroundColor: 'rgba(103, 49, 79, 0.04)',
                  },
                }}
              >
                Change password
              </Button>
            </Box>

            {/* Availability Schedule Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Typography sx={{
                  fontFamily: DESIGN_TOKENS.typography.fontFamily,
                  fontSize: '20px',
                  fontWeight: 700,
                  color: DESIGN_TOKENS.colors.text,
                }}>
                  Availability Schedule
                </Typography>
                <Typography sx={{
                  fontFamily: DESIGN_TOKENS.typography.fontFamily,
                  fontSize: '16px',
                  color: DESIGN_TOKENS.colors.textSecondary,
                }}>
                  Set your business days and hours for when customers can order your services.
                </Typography>
              </Box>

              {/* Business Hours Display */}
              {loadingSchedule ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                  <CircularProgress size={24} sx={{ color: DESIGN_TOKENS.colors.primary }} />
                </Box>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  gap: 0,
                  '@media (max-width: 768px)': {
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                  },
                }}>
                  {businessDays.map((day, index) => (
                    <Box key={day.day} sx={{ 
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      padding: '4px 0',
                      '@media (max-width: 768px)': {
                        minWidth: '60px',
                      },
                    }}>
                      <Typography sx={{
                        fontFamily: DESIGN_TOKENS.typography.fontFamily,
                        fontSize: '16px',
                        color: DESIGN_TOKENS.colors.textSecondary,
                        width: '50px',
                        '@media (max-width: 768px)': {
                          fontSize: '14px',
                          width: '100%',
                          textAlign: 'center',
                        },
                      }}>
                        {day.day}
                      </Typography>
                      <Typography sx={{
                        fontFamily: DESIGN_TOKENS.typography.fontFamily,
                        fontSize: '13px',
                        color: DESIGN_TOKENS.colors.textSecondary,
                        textAlign: 'center',
                        '@media (max-width: 768px)': {
                          fontSize: '11px',
                        },
                      }}>
                        {day.enabled ? `${day.from}–\n${day.to}` : '–'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Unavailable Dates */}
              {loadingUnavailable ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                  <CircularProgress size={24} sx={{ color: DESIGN_TOKENS.colors.primary }} />
                </Box>
              ) : unavailableDates.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Typography sx={{
                    fontFamily: DESIGN_TOKENS.typography.fontFamily,
                    fontSize: '13px',
                    color: DESIGN_TOKENS.colors.textSecondary,
                  }}>
                    Unavailable dates
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {unavailableDates.map((date) => (
                      <Box key={date.id} sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid #F1F4F4',
                        padding: '8px 0',
                      }}>
                        <Typography sx={{
                          fontFamily: DESIGN_TOKENS.typography.fontFamily,
                          fontSize: '16px',
                          color: DESIGN_TOKENS.colors.textSecondary,
                        }}>
                          {date.description}
                        </Typography>
                        <IconButton
                          onClick={() => handleDeleteUnavailableDate(date.id)}
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: '16px',
                            '&:hover': { backgroundColor: 'rgba(103, 49, 79, 0.04)' },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 20, color: DESIGN_TOKENS.colors.primary }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : null}

              {/* Action Buttons */}
              <Box sx={{ 
                display: 'flex', 
                gap: '16px',
                '@media (max-width: 768px)': {
                  flexDirection: 'column',
                },
              }}>
                <Button
                  variant="outlined"
                  onClick={() => setEditScheduleOpen(true)}
                  sx={{
                    height: '56px',
                    borderRadius: '16px',
                    border: '1px solid #67314F',
                    color: DESIGN_TOKENS.colors.primary,
                    fontFamily: DESIGN_TOKENS.typography.fontFamily,
                    fontSize: '16px',
                    fontWeight: 700,
                    padding: '2px 32px 0px',
                    textTransform: 'none',
                    '&:hover': {
                      border: '1px solid #67314F',
                      backgroundColor: 'rgba(103, 49, 79, 0.04)',
                    },
                    '@media (max-width: 768px)': {
                      width: '100%',
                    },
                  }}
                >
                  Edit schedule
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setAddUnavailabilityOpen(true)}
                  sx={{
                    height: '56px',
                    borderRadius: '16px',
                    border: '1px solid #67314F',
                    color: DESIGN_TOKENS.colors.primary,
                    fontFamily: DESIGN_TOKENS.typography.fontFamily,
                    fontSize: '16px',
                    fontWeight: 700,
                    padding: '2px 32px 0px',
                    textTransform: 'none',
                    '&:hover': {
                      border: '1px solid #67314F',
                      backgroundColor: 'rgba(103, 49, 79, 0.04)',
                    },
                    '@media (max-width: 768px)': {
                      width: '100%',
                      fontSize: '14px',
                      padding: '2px 16px 0px',
                    },
                  }}
                >
                  Add unavailability period
                </Button>
              </Box>
            </Box>

            {/* Deactivate Profile Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Typography sx={{
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '20px',
                fontWeight: 700,
                color: DESIGN_TOKENS.colors.text,
              }}>
                Deactivate profile
              </Typography>
              <Typography sx={{
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '16px',
                color: DESIGN_TOKENS.colors.textSecondary,
              }}>
                You can temporarily hide your profile from Eventi listings to stop displaying your services and receiving orders. Reactivate at any time.
              </Typography>
              
              {isProfileDeactivated ? (
                <>
                  <Box sx={{
                    display: 'inline-flex',
                    padding: '0px 12px',
                    height: '32px',
                    alignItems: 'center',
                    borderRadius: '8px',
                    backgroundColor: '#925B64',
                    width: 'fit-content',
                  }}>
                    <Typography sx={{
                      fontFamily: DESIGN_TOKENS.typography.fontFamily,
                      fontSize: '13px',
                      color: '#FFFFFF',
                    }}>
                      Profile deactivated
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={handleToggleDeactivation}
                    sx={{
                      width: 'fit-content',
                      height: '56px',
                      borderRadius: '16px',
                      border: '1px solid #67314F',
                      color: DESIGN_TOKENS.colors.primary,
                      fontFamily: DESIGN_TOKENS.typography.fontFamily,
                      fontSize: '16px',
                      fontWeight: 700,
                      padding: '2px 32px 0px',
                      textTransform: 'none',
                      '&:hover': {
                        border: '1px solid #67314F',
                        backgroundColor: 'rgba(103, 49, 79, 0.04)',
                      },
                    }}
                  >
                    Reactivate
                  </Button>
                </>
              ) : (
                <Button
                  variant="outlined"
                  onClick={handleToggleDeactivation}
                  sx={{
                    width: 'fit-content',
                    height: '56px',
                    borderRadius: '16px',
                    border: '1px solid #67314F',
                    color: DESIGN_TOKENS.colors.primary,
                    fontFamily: DESIGN_TOKENS.typography.fontFamily,
                    fontSize: '16px',
                    fontWeight: 700,
                    padding: '2px 32px 0px',
                    textTransform: 'none',
                    '&:hover': {
                      border: '1px solid #67314F',
                      backgroundColor: 'rgba(103, 49, 79, 0.04)',
                    },
                  }}
                >
                  Deactivate profile
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {/* Change Password Modal */}
        <Dialog
          open={changePasswordOpen}
          onClose={() => setChangePasswordOpen(false)}
          PaperProps={{
            sx: {
              width: '508px',
              height: '464px',
              borderRadius: '12px',
              boxShadow: '0px 80px 250px 0px rgba(26, 109, 109, 0.1)',
              padding: '24px',
              '@media (max-width: 768px)': {
                width: '100%',
                maxWidth: 'calc(100vw - 32px)',
                margin: '16px',
                padding: '16px',
                height: 'auto',
              },
            },
          }}
        >
          <DialogTitle sx={{
            fontFamily: DESIGN_TOKENS.typography.fontFamily,
            fontSize: '22px',
            fontWeight: 700,
            color: DESIGN_TOKENS.colors.text,
            padding: 0,
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            Change password
            <IconButton
              onClick={() => setChangePasswordOpen(false)}
              sx={{
                backgroundColor: '#F6F8F8',
                borderRadius: '40px',
                width: 40,
                height: 40,
                padding: '8px',
                '&:hover': { backgroundColor: '#E5E9E9' },
              }}
            >
              <CloseIcon sx={{ fontSize: 24, color: DESIGN_TOKENS.colors.text }} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ padding: 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <TextField
              fullWidth
              type={showCurrentPassword ? 'text' : 'password'}
              placeholder="Current password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end">
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#F6F8F8',
                  borderRadius: '12px',
                  height: '56px',
                  fontFamily: DESIGN_TOKENS.typography.fontFamily,
                  '& fieldset': { border: 'none' },
                },
              }}
            />
            <TextField
              fullWidth
              type={showNewPassword ? 'text' : 'password'}
              placeholder="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#F6F8F8',
                  borderRadius: '12px',
                  height: '56px',
                  fontFamily: DESIGN_TOKENS.typography.fontFamily,
                  '& fieldset': { border: 'none' },
                },
              }}
            />
            <TextField
              fullWidth
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#F6F8F8',
                  borderRadius: '12px',
                  height: '56px',
                  fontFamily: DESIGN_TOKENS.typography.fontFamily,
                  '& fieldset': { border: 'none' },
                },
              }}
            />
            <Button
              fullWidth
              variant="contained"
              onClick={handleChangePassword}
              sx={{
                height: '56px',
                borderRadius: '16px',
                backgroundColor: DESIGN_TOKENS.colors.primary,
                color: '#FFFFFF',
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '16px',
                fontWeight: 700,
                textTransform: 'none',
                marginTop: '8px',
                '&:hover': { backgroundColor: DESIGN_TOKENS.colors.primaryDark },
              }}
            >
              Update password
            </Button>
            <Button
              fullWidth
              variant="text"
              onClick={() => setChangePasswordOpen(false)}
              sx={{
                height: '56px',
                borderRadius: '16px',
                color: DESIGN_TOKENS.colors.primary,
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '16px',
                fontWeight: 700,
                textTransform: 'none',
                '&:hover': { backgroundColor: 'rgba(103, 49, 79, 0.04)' },
              }}
            >
              Cancel
            </Button>
          </DialogContent>
        </Dialog>

        {/* Edit Schedule Modal */}
        <Dialog
          open={editScheduleOpen}
          onClose={() => setEditScheduleOpen(false)}
          PaperProps={{
            sx: {
              width: '508px',
              maxHeight: '906px',
              borderRadius: '12px',
              boxShadow: '0px 80px 250px 0px rgba(26, 109, 109, 0.1)',
              padding: '24px',
              '@media (max-width: 768px)': {
                width: '100%',
                maxWidth: 'calc(100vw - 32px)',
                margin: '16px',
                padding: '16px',
                maxHeight: 'calc(100vh - 32px)',
              },
            },
          }}
        >
          <DialogTitle sx={{
            fontFamily: DESIGN_TOKENS.typography.fontFamily,
            fontSize: '22px',
            fontWeight: 700,
            color: DESIGN_TOKENS.colors.text,
            padding: 0,
            marginBottom: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            Edit schedule
            <IconButton
              onClick={() => setEditScheduleOpen(false)}
              sx={{
                backgroundColor: '#F6F8F8',
                borderRadius: '40px',
                width: 40,
                height: 40,
                padding: '8px',
                '&:hover': { backgroundColor: '#E5E9E9' },
              }}
            >
              <CloseIcon sx={{ fontSize: 24, color: DESIGN_TOKENS.colors.text }} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ padding: 0, display: 'flex', flexDirection: 'column', gap: '32px' }}>
            <Typography sx={{
              fontFamily: DESIGN_TOKENS.typography.fontFamily,
              fontSize: '16px',
              color: DESIGN_TOKENS.colors.textSecondary,
            }}>
              Set your business days and hours for when customers can order your services.
            </Typography>

            {/* Same Hours Checkbox */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sameHoursForAll}
                    onChange={(e) => setSameHoursForAll(e.target.checked)}
                    sx={{
                      color: DESIGN_TOKENS.colors.textSecondary,
                      '&.Mui-checked': { color: DESIGN_TOKENS.colors.primary },
                    }}
                  />
                }
                label="Same working hours for all business days"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontFamily: DESIGN_TOKENS.typography.fontFamily,
                    fontSize: '16px',
                  },
                }}
              />

              {sameHoursForAll && (
                <Box sx={{ display: 'flex', gap: '16px' }}>
                  <TextField
                    fullWidth
                    type="time"
                    label="From"
                    value={businessDays.find(d => d.enabled)?.from || '09:00'}
                    onChange={(e) => {
                      const newDays = businessDays.map(day => 
                        day.enabled ? { ...day, from: e.target.value } : day
                      );
                      setBusinessDays(newDays);
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#F6F8F8',
                        borderRadius: '12px',
                        height: '56px',
                        fontFamily: DESIGN_TOKENS.typography.fontFamily,
                        fontSize: '14px',
                        '& fieldset': { border: 'none' },
                        '& input': {
                          fontSize: '14px',
                        },
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    type="time"
                    label="To"
                    value={businessDays.find(d => d.enabled)?.to || '17:00'}
                    onChange={(e) => {
                      const newDays = businessDays.map(day => 
                        day.enabled ? { ...day, to: e.target.value } : day
                      );
                      setBusinessDays(newDays);
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#F6F8F8',
                        borderRadius: '12px',
                        height: '56px',
                        fontFamily: DESIGN_TOKENS.typography.fontFamily,
                        fontSize: '14px',
                        '& fieldset': { border: 'none' },
                        '& input': {
                          fontSize: '14px',
                        },
                      },
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* Business Days */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Typography sx={{
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '16px',
                color: DESIGN_TOKENS.colors.text,
              }}>
                Business days
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {businessDays.map((day, index) => (
                  <Box key={day.day} sx={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={day.enabled}
                          onChange={() => handleDayToggle(index)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#FFFFFF',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: DESIGN_TOKENS.colors.primary,
                            },
                          }}
                        />
                      }
                      label={day.day}
                      sx={{
                        flex: 1,
                        '& .MuiFormControlLabel-label': {
                          fontFamily: DESIGN_TOKENS.typography.fontFamily,
                          fontSize: '16px',
                        },
                      }}
                    />
                    {!sameHoursForAll && day.enabled && (
                      <>
                        <TextField
                          type="time"
                          value={day.from}
                          onChange={(e) => handleTimeChange(index, 'from', e.target.value)}
                          label="From"
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            width: '120px',
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#F6F8F8',
                              borderRadius: '12px',
                              height: '48px',
                              fontFamily: DESIGN_TOKENS.typography.fontFamily,
                              fontSize: '12.5px',
                              '& fieldset': { border: 'none' },
                              '& input': {
                                fontSize: '12.5px',
                              },
                            },
                          }}
                        />
                        <TextField
                          type="time"
                          value={day.to}
                          onChange={(e) => handleTimeChange(index, 'to', e.target.value)}
                          label="To"
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            width: '120px',
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#F6F8F8',
                              borderRadius: '12px',
                              height: '48px',
                              fontFamily: DESIGN_TOKENS.typography.fontFamily,
                              fontSize: '12.5px',
                              '& fieldset': { border: 'none' },
                              '& input': {
                                fontSize: '12.5px',
                              },
                            },
                          }}
                        />
                      </>
                    )}
                    {!sameHoursForAll && !day.enabled && (
                      <Box sx={{ width: '256px' }} />
                    )}
                  </Box>
                ))}
              </Box>
            </Box>

            <Button
              fullWidth
              variant="contained"
              onClick={handleSaveSchedule}
              disabled={savingSchedule}
              sx={{
                height: '56px',
                borderRadius: '16px',
                backgroundColor: DESIGN_TOKENS.colors.primary,
                color: '#FFFFFF',
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '16px',
                fontWeight: 700,
                textTransform: 'none',
                '&:hover': { backgroundColor: DESIGN_TOKENS.colors.primaryDark },
                '&:disabled': { backgroundColor: '#F1F4F4', color: '#C5CBD3' },
              }}
            >
              {savingSchedule ? 'Saving...' : 'Save changes'}
            </Button>
            <Button
              fullWidth
              variant="text"
              onClick={() => setEditScheduleOpen(false)}
              sx={{
                height: '56px',
                borderRadius: '16px',
                color: DESIGN_TOKENS.colors.primary,
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '16px',
                fontWeight: 700,
                textTransform: 'none',
                '&:hover': { backgroundColor: 'rgba(103, 49, 79, 0.04)' },
              }}
            >
              Cancel
            </Button>
          </DialogContent>
        </Dialog>

        {/* Add Unavailability Period Modal */}
        <Dialog
          open={addUnavailabilityOpen}
          onClose={() => setAddUnavailabilityOpen(false)}
          PaperProps={{
            sx: {
              width: '780px',
              borderRadius: '12px',
              boxShadow: '0px 80px 250px 0px rgba(26, 109, 109, 0.1)',
              padding: '24px',
              '@media (max-width: 768px)': {
                width: '100%',
                maxWidth: 'calc(100vw - 32px)',
                margin: '16px',
                padding: '16px',
              },
            },
          }}
        >
          <DialogTitle sx={{
            fontFamily: DESIGN_TOKENS.typography.fontFamily,
            fontSize: '22px',
            fontWeight: 700,
            color: DESIGN_TOKENS.colors.text,
            padding: 0,
            marginBottom: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            Add unavailability period
            <IconButton
              onClick={() => setAddUnavailabilityOpen(false)}
              sx={{
                backgroundColor: '#F6F8F8',
                borderRadius: '40px',
                width: 40,
                height: 40,
                padding: '8px',
                '&:hover': { backgroundColor: '#E5E9E9' },
              }}
            >
              <CloseIcon sx={{ fontSize: 24, color: DESIGN_TOKENS.colors.text }} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ padding: 0, display: 'flex', flexDirection: 'column', gap: '32px' }}>
            {/* Dual Calendar View */}
            <Box sx={{ 
              display: 'flex', 
              gap: '24px',
              justifyContent: 'space-between',
              '@media (max-width: 768px)': {
                flexDirection: 'column',
                gap: '16px',
              },
            }}>
              <Box sx={{ flex: 1 }}>
                <Calendar
                  selectedStartDate={selectedStartDate}
                  selectedEndDate={selectedEndDate}
                  onDateSelect={handleDateSelect}
                  onRangeSelect={handleRangeSelect}
                  month={firstCalendarMonth}
                  year={firstCalendarYear}
                  onMonthChange={handleFirstCalendarMonthChange}
                  showNavigation={true}
                  disabledDays={[5, 6]} // Saturday and Sunday
                  mode="range"
                />
              </Box>
              <Box sx={{ 
                flex: 1,
                '@media (max-width: 768px)': {
                  display: 'none',
                },
              }}>
                <Calendar
                  selectedStartDate={selectedStartDate}
                  selectedEndDate={selectedEndDate}
                  onDateSelect={handleDateSelect}
                  onRangeSelect={handleRangeSelect}
                  month={secondCalendarMonth}
                  year={secondCalendarYear}
                  onMonthChange={handleSecondCalendarMonthChange}
                  showNavigation={false}
                  disabledDays={[5, 6]} // Saturday and Sunday
                  mode="range"
                />
              </Box>
            </Box>

            {/* Repeat Options */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <Box sx={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
                <Typography sx={{
                  fontFamily: DESIGN_TOKENS.typography.fontFamily,
                  fontSize: '16px',
                  color: DESIGN_TOKENS.colors.textSecondary,
                }}>
                  Repeat every
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={repeatPeriod === 'none'}
                      onChange={() => setRepeatPeriod('none')}
                      sx={{
                        color: DESIGN_TOKENS.colors.textSecondary,
                        '&.Mui-checked': { color: DESIGN_TOKENS.colors.primary },
                      }}
                    />
                  }
                  label="none"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontFamily: DESIGN_TOKENS.typography.fontFamily,
                      fontSize: '16px',
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={repeatPeriod === 'monthly'}
                      onChange={() => setRepeatPeriod('monthly')}
                      sx={{
                        color: DESIGN_TOKENS.colors.textSecondary,
                        '&.Mui-checked': { color: DESIGN_TOKENS.colors.primary },
                      }}
                    />
                  }
                  label="month"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontFamily: DESIGN_TOKENS.typography.fontFamily,
                      fontSize: '16px',
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={repeatPeriod === 'yearly'}
                      onChange={() => setRepeatPeriod('yearly')}
                      sx={{
                        color: DESIGN_TOKENS.colors.textSecondary,
                        '&.Mui-checked': { color: DESIGN_TOKENS.colors.primary },
                      }}
                    />
                  }
                  label="year"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontFamily: DESIGN_TOKENS.typography.fontFamily,
                      fontSize: '16px',
                    },
                  }}
                />
              </Box>
              <Typography sx={{
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '16px',
                fontWeight: 700,
                color: DESIGN_TOKENS.colors.textSecondary,
              }}>
                {selectedStartDate && selectedEndDate
                  ? `${selectedStartDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}–${selectedEndDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}${repeatPeriod !== 'none' ? ` every ${repeatPeriod === 'monthly' ? 'month' : 'year'}` : ''}`
                  : 'Select dates to block'}
              </Typography>
            </Box>

            <Button
              fullWidth
              variant="contained"
              onClick={handleBlockDates}
              disabled={!selectedStartDate || !selectedEndDate || addingUnavailable}
              sx={{
                height: '60px',
                borderRadius: '16px',
                backgroundColor: DESIGN_TOKENS.colors.primary,
                color: '#FFFFFF',
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '16px',
                fontWeight: 700,
                textTransform: 'none',
                '&:hover': { backgroundColor: DESIGN_TOKENS.colors.primaryDark },
                '&:disabled': { backgroundColor: '#F1F4F4', color: '#C5CBD3' },
              }}
            >
              {addingUnavailable ? 'Adding...' : 'Block dates'}
            </Button>
          </DialogContent>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Settings;

